import {
  downloadShippingLabelUrl,
  downloadShippingLabelsIndividually,
  mergeShippingLabelUrls,
} from './shippingLabelUtils'

export type OrderPackingSlipDownloadType = 'PACKING_SLIP' | 'SHIPPING_LABEL' | 'BOTH'

export type OrderPackingSlipItemType = 'PACKING_SLIP' | 'SHIPPING_LABEL'

export type OrderPackingSlipItem = {
  order_id: number
  type: OrderPackingSlipItemType
  url?: string | null
  urls?: string[] | null
}

export type OrderPackingSlipDownloadPayload = {
  items: OrderPackingSlipItem[]
  urls: string[]
  message: string
  shippingLabelRequestMessage?: string | null
}

export type ParsedOrderDocumentDownload = {
  items: OrderPackingSlipItem[]
  urls: string[]
}

const normalizeDocumentUrl = (value: unknown): string => {
  return typeof value === 'string' ? value.trim() : ''
}

const normalizeUrlArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((entry) => normalizeDocumentUrl(entry))
    .filter((url) => url.length > 0)
}

const normalizeOrderPackingSlipItem = (value: unknown): OrderPackingSlipItem | null => {
  if (!value || typeof value !== 'object') {
    return null
  }

  const record = value as Record<string, unknown>
  const type = record.type
  const orderId = Number(record.order_id)

  if (
    !Number.isFinite(orderId) ||
    (type !== 'PACKING_SLIP' && type !== 'SHIPPING_LABEL')
  ) {
    return null
  }

  return {
    order_id: orderId,
    type,
    url: typeof record.url === 'string' ? record.url : null,
    urls: Array.isArray(record.urls)
      ? record.urls.filter((entry): entry is string => typeof entry === 'string')
      : null,
  }
}

const normalizeLegacyShippingLabelRow = (value: unknown): OrderPackingSlipItem | null => {
  if (!value || typeof value !== 'object') {
    return null
  }

  const record = value as Record<string, unknown>
  const orderId = Number(record.order_id)

  if (!Number.isFinite(orderId)) {
    return null
  }

  const labelUrls = normalizeUrlArray(record.label_url ?? record.label_urls)

  return {
    order_id: orderId,
    type: 'SHIPPING_LABEL',
    url: null,
    urls: labelUrls.length > 0 ? labelUrls : null,
  }
}

const parseLegacyShippingLabelRows = (data: unknown): OrderPackingSlipItem[] => {
  if (!Array.isArray(data)) {
    return []
  }

  return data
    .map(normalizeLegacyShippingLabelRow)
    .filter((item): item is OrderPackingSlipItem => item !== null)
}

export const parseOrderPackingSlipItems = (data: unknown): OrderPackingSlipItem[] => {
  if (Array.isArray(data)) {
    const typedItems = data
      .map(normalizeOrderPackingSlipItem)
      .filter((item): item is OrderPackingSlipItem => item !== null)

    if (typedItems.length > 0) {
      return typedItems
    }

    return parseLegacyShippingLabelRows(data)
  }

  if (!data || typeof data !== 'object') {
    return []
  }

  const items = (data as Record<string, unknown>).items
  if (!Array.isArray(items)) {
    return []
  }

  return items
    .map(normalizeOrderPackingSlipItem)
    .filter((item): item is OrderPackingSlipItem => item !== null)
}

/** Collect PDF URLs in the exact sequence returned by the API items array. */
export const extractUrlsFromPackingSlipItem = (item: OrderPackingSlipItem): string[] => {
  if (item.type === 'PACKING_SLIP') {
    const url = normalizeDocumentUrl(item.url)
    return url ? [url] : []
  }

  const labelUrls: string[] = []

  if (Array.isArray(item.urls)) {
    for (const url of item.urls) {
      const normalized = normalizeDocumentUrl(url)
      if (normalized) {
        labelUrls.push(normalized)
      }
    }
  }

  const fallbackUrl = normalizeDocumentUrl(item.url)
  if (!labelUrls.length && fallbackUrl) {
    labelUrls.push(fallbackUrl)
  }

  return labelUrls
}

export const extractDocumentUrlsFromItems = (items: OrderPackingSlipItem[]): string[] => {
  const urls: string[] = []

  for (const item of items) {
    urls.push(...extractUrlsFromPackingSlipItem(item))
  }

  return urls
}

/** Normalize all supported API response shapes into ordered items and urls. */
export const parseOrderDocumentDownloadData = (data: unknown): ParsedOrderDocumentDownload => {
  const items = parseOrderPackingSlipItems(data)
  const urlsFromItems = extractDocumentUrlsFromItems(items)

  if (urlsFromItems.length > 0) {
    return {items, urls: urlsFromItems}
  }

  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const record = data as Record<string, unknown>
    const packingSlipUrls = normalizeUrlArray(record.packing_slip_urls)

    if (packingSlipUrls.length > 0) {
      return {items: [], urls: packingSlipUrls}
    }
  }

  const legacyItems = parseLegacyShippingLabelRows(data)
  const legacyUrls = extractDocumentUrlsFromItems(legacyItems)

  if (legacyUrls.length > 0) {
    return {items: legacyItems, urls: legacyUrls}
  }

  return {items: [], urls: []}
}

export const countMissingShippingLabels = (items: OrderPackingSlipItem[]): number =>
  items.filter(
    (item) => item.type === 'SHIPPING_LABEL' && extractUrlsFromPackingSlipItem(item).length === 0
  ).length

export const getOrderPackingSlipDownloadFilename = (type: OrderPackingSlipDownloadType): string => {
  const timestamp = new Date().getTime()

  switch (type) {
    case 'PACKING_SLIP':
      return `packing-slips-${timestamp}.pdf`
    case 'SHIPPING_LABEL':
      return `shipping-labels-${timestamp}.pdf`
    case 'BOTH':
      return `packing-slips-and-labels-${timestamp}.pdf`
    default:
      return `order-documents-${timestamp}.pdf`
  }
}

const triggerPdfBlobDownload = (pdfBytes: Uint8Array, filename: string) => {
  const blob = new Blob([pdfBytes.buffer], {type: 'application/pdf'})
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 100)
}

export const downloadMergedOrderDocuments = async (
  urls: string[],
  filename: string
): Promise<{success: boolean; failedUrlCount: number}> => {
  if (!urls.length) {
    return {success: false, failedUrlCount: 0}
  }

  if (urls.length === 1) {
    const downloaded = await downloadShippingLabelUrl(urls[0], filename)
    return {success: downloaded, failedUrlCount: downloaded ? 0 : 1}
  }

  try {
    const {pdfBytes, failedUrls} = await mergeShippingLabelUrls(urls)
    triggerPdfBlobDownload(pdfBytes, filename)

    if (failedUrls.length > 0) {
      const {successCount, failedCount} = await downloadShippingLabelsIndividually(failedUrls)
      return {
        success: true,
        failedUrlCount: failedUrls.length - successCount + failedCount,
      }
    }

    return {success: true, failedUrlCount: 0}
  } catch (error) {
    console.error('Failed to merge order documents. Falling back to individual downloads.', error)
    const {successCount, failedCount} = await downloadShippingLabelsIndividually(urls)
    return {success: successCount > 0, failedUrlCount: failedCount}
  }
}

export const downloadConsolidatedOrderDocumentsFromItems = async (
  parsed: ParsedOrderDocumentDownload,
  filename: string
): Promise<{success: boolean; failedUrlCount: number; urlCount: number}> => {
  const urls =
    parsed.urls.length > 0 ? parsed.urls : extractDocumentUrlsFromItems(parsed.items)
  const result = await downloadMergedOrderDocuments(urls, filename)
  return {...result, urlCount: urls.length}
}
